import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import { isArchivedStatus } from '@/features/inventory/itemStatus';
import {
  postStockMovement,
  type AdjustmentReason,
  type InventoryLevelRow,
  type Item,
} from '@/features/inventory/hooks';

/**
 * Physical cycle count: a worker picks a location, walks the floor with
 * the sheet open on a tablet, and types what is physically there for
 * every item. Nothing posts until the count is reviewed. Each line whose
 * count differs from the system becomes one `cycle_count` stock movement,
 * all sharing `reference_type = 'cycle_count'` and the count's id.
 *
 * The sheet in progress lives in localStorage on the device doing the
 * count, so a tablet that sleeps or reloads picks up where it left off.
 */

export type CountUnit = 'base' | 'stocked';

/** One item × location × lot bucket on the count sheet. */
export interface CycleCountLine {
  key: string;
  itemId: string;
  sku: string;
  name: string;
  baseUom: string;
  kinds: string[];
  isLotTracked: boolean;
  locationId: string;
  lotNumber: string | null;
  /** System on-hand right now, in the base UoM. */
  onHand: number;
  /** True when the item has no bucket at this location yet. */
  notStockedHere: boolean;
  /** Added by the counter (a lot that isn't in the system yet). */
  added: boolean;
}

export interface CycleCountEntry {
  /** As typed, in `unit`. */
  counted: string;
  unit: CountUnit;
  /**
   * System on-hand when the count was typed. The posted change is
   * counted − this, so stock that moves after the shelf was counted
   * (a receipt, a production run) isn't wiped out by the adjustment.
   */
  systemAtCount: number;
  reason: AdjustmentReason;
  note: string;
}

export interface CycleCountAddedLine {
  itemId: string;
  lotNumber: string;
}

export interface CycleCountDraft {
  id: string;
  startedAt: string;
  locationId: string;
  entries: Record<string, CycleCountEntry>;
  addedLines: CycleCountAddedLine[];
  note: string;
}

export function cycleCountLineKey(
  itemId: string,
  locationId: string,
  lotNumber: string | null,
): string {
  return `${itemId}|${locationId}|${lotNumber ?? ''}`;
}

/**
 * Every countable line at one location: each existing bucket there, plus
 * a zero line for any active item with no stock at the location so stock
 * found on the shelf can be counted in. Archived items are left out.
 */
export function buildCycleCountLines(
  items: Item[],
  levels: InventoryLevelRow[],
  locationId: string,
  addedLines: CycleCountAddedLine[],
): CycleCountLine[] {
  const active = new Map(
    items.filter((it) => !isArchivedStatus(it.status)).map((it) => [it.id, it]),
  );
  const lines = new Map<string, CycleCountLine>();
  const base = (it: Item) => ({
    itemId: it.id,
    sku: it.sku,
    name: it.name,
    baseUom: it.base_uom,
    kinds: [it.kind, ...(it.additional_kinds ?? [])],
    isLotTracked: it.is_lot_tracked,
    locationId,
  });

  for (const lvl of levels) {
    if (lvl.location_id !== locationId) continue;
    const it = active.get(lvl.item_id);
    if (!it) continue;
    // A lot we can't read back by number can't be posted to by number either.
    if (lvl.lot_id && !lvl.lots?.lot_number) continue;
    const lotNumber = lvl.lots?.lot_number ?? null;
    const key = cycleCountLineKey(it.id, locationId, lotNumber);
    const existing = lines.get(key);
    if (existing) {
      existing.onHand += Number(lvl.quantity ?? 0);
      continue;
    }
    lines.set(key, {
      ...base(it),
      key,
      lotNumber,
      onHand: Number(lvl.quantity ?? 0),
      notStockedHere: false,
      added: false,
    });
  }

  const stockedHere = new Set([...lines.values()].map((l) => l.itemId));
  for (const it of active.values()) {
    if (stockedHere.has(it.id)) continue;
    const key = cycleCountLineKey(it.id, locationId, null);
    lines.set(key, {
      ...base(it),
      key,
      lotNumber: null,
      onHand: 0,
      notStockedHere: true,
      added: false,
    });
  }

  for (const add of addedLines) {
    const it = active.get(add.itemId);
    if (!it) continue;
    const key = cycleCountLineKey(it.id, locationId, add.lotNumber);
    if (lines.has(key)) continue;
    lines.set(key, {
      ...base(it),
      key,
      lotNumber: add.lotNumber,
      onHand: 0,
      notStockedHere: true,
      added: true,
    });
  }

  return [...lines.values()].sort(
    (a, b) =>
      a.sku.localeCompare(b.sku, undefined, { numeric: true }) ||
      (a.lotNumber ?? '').localeCompare(b.lotNumber ?? '', undefined, { numeric: true }),
  );
}

/** The typed count in base units, or null when the field is blank or not a valid count. */
export function countedBaseQty(
  entry: CycleCountEntry | undefined,
  unitsPerStockedUnit: number,
): number | null {
  if (!entry) return null;
  const raw = entry.counted.trim().replace(/,/g, '');
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(entry.unit === 'stocked' ? n * unitsPerStockedUnit : n);
}

// -----------------------------------------------------------------------
// Draft persistence
// -----------------------------------------------------------------------

function draftStorageKey(organizationId: string, userId: string): string {
  return `atlas:cycle-count:${organizationId}:${userId}`;
}

export function loadCycleCountDraft(
  organizationId: string,
  userId: string,
): CycleCountDraft | null {
  try {
    const raw = window.localStorage.getItem(draftStorageKey(organizationId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CycleCountDraft>;
    if (!parsed || typeof parsed.id !== 'string' || typeof parsed.locationId !== 'string')
      return null;
    return {
      id: parsed.id,
      startedAt: parsed.startedAt ?? new Date().toISOString(),
      locationId: parsed.locationId,
      entries: parsed.entries ?? {},
      addedLines: parsed.addedLines ?? [],
      note: parsed.note ?? '',
    };
  } catch {
    return null;
  }
}

function isEmptyDraft(draft: CycleCountDraft): boolean {
  return (
    Object.keys(draft.entries).length === 0 && draft.addedLines.length === 0 && !draft.note.trim()
  );
}

export function saveCycleCountDraft(
  organizationId: string,
  userId: string,
  draft: CycleCountDraft | null,
) {
  try {
    const key = draftStorageKey(organizationId, userId);
    if (draft && !isEmptyDraft(draft)) window.localStorage.setItem(key, JSON.stringify(draft));
    else window.localStorage.removeItem(key);
  } catch {
    // Private mode or a full quota: the count still works, it just won't survive a reload.
  }
}

export function hasCycleCountInProgress(
  organizationId: string | undefined,
  userId: string | undefined,
): boolean {
  if (!organizationId || !userId) return false;
  const draft = loadCycleCountDraft(organizationId, userId);
  return !!draft && Object.keys(draft.entries).length > 0;
}

export function newCycleCountDraft(locationId: string): CycleCountDraft {
  return {
    id: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
    locationId,
    entries: {},
    addedLines: [],
    note: '',
  };
}

// -----------------------------------------------------------------------
// Posting
// -----------------------------------------------------------------------

export interface CycleCountAdjustment {
  key: string;
  itemId: string;
  locationId: string;
  lotNumber: string | null;
  /** Signed change in base units; never zero. */
  delta: number;
  reason: AdjustmentReason;
  memo: string | null;
}

/**
 * Post one `cycle_count` movement per variance line, in order. Lines are
 * posted one at a time so `onPosted` can drop each from the draft as it
 * lands; if one fails, the error is thrown and the lines still in the
 * draft are exactly the ones left to post.
 */
export function usePostCycleCount() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      countId: string;
      adjustments: CycleCountAdjustment[];
      onPosted: (key: string) => void;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      let posted = 0;
      for (const adj of input.adjustments) {
        if (adj.delta === 0) continue;
        await postStockMovement(profile.organization_id, {
          itemId: adj.itemId,
          type: 'cycle_count',
          quantity: Math.abs(adj.delta),
          toLocationId: adj.delta > 0 ? adj.locationId : null,
          fromLocationId: adj.delta < 0 ? adj.locationId : null,
          lotNumber: adj.lotNumber,
          reasonCode: adj.reason,
          memo: adj.memo,
          referenceType: 'cycle_count',
          referenceId: input.countId,
        });
        posted += 1;
        input.onPosted(adj.key);
      }
      return { posted };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}
